import { useEffect } from "react";

import { ErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { SIBreadcrumbs } from "app/pages/sales-intelligence/common-components/SIBreadcrumbs/SIBreadcrumbs";
import {
    INTENT_SIGNALS_MANAGEMENT_INTRO_PAGE_ROUTE,
    INTENT_SIGNALS_MANAGEMENT_INTRO_PAGE_URL,
    INTENT_SIGNALS_MANAGEMENT_PAGE_ROUTE,
} from "app/pages/sales-intelligence/constants/routes";
import withSecondaryBarSet from "app/pages/sales-intelligence/hoc/withSecondaryBarSet";
import ApplyTopicModal from "app/pages/sales-intelligence/pages/manage-signals/components/ApplyTopicModal/ApplyTopicModal";
import ApplyDataAgreementsContent from "app/pages/sales-intelligence/pages/manage-signals/components/ApplyTopicModal/modalContent/ApplyDataAgreementsContent";
import TermsAndConditionsContent from "app/pages/sales-intelligence/pages/manage-signals/components/ApplyTopicModal/modalContent/TermsAndConditionsContent";
import IntroIntentSignalPage from "app/pages/sales-intelligence/pages/manage-signals/components/ManageIntentSignalsPage/IntroIntentSignalPage";
import { INTENT_SIGNALS_BREADCRUMBS } from "app/pages/sales-intelligence/pages/manage-signals/constants/breadcrumbs";
import {
    approvalModalConfig,
    dataAgreementsModalConfig,
} from "app/pages/sales-intelligence/pages/manage-signals/styles";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import intentTrackingService from "app/pages/sales-intelligence/services/tracking/intent/trackingService";
import useIntentManagementApprovedAction from "app/pages/sales-intelligence/sub-modules/intent/hooks/useIntentManagementApprovedAction";
import {
    fetchIntentUserAdminsThunk,
    fetchSelectedIntentTopicsThunk,
} from "app/pages/sales-intelligence/sub-modules/intent/store/management/effects/effects";
import {
    selectIntentSelectedTopics,
    selectIsSelectedTopicFetching,
    selectIsUserApprovedTerms,
} from "app/pages/sales-intelligence/sub-modules/intent/store/management/selectors/selectors";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import { PageLoader } from "pro-features/components/Loaders/PageLoader";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { PageErrorState } from "./components/ErrorState/PageErrorState";
import IntentTopicView from "./components/IntentTopicView/IntentTopicView";
import IntroductionView from "./components/IntroductionView/IntroductionView";
import ManageIntentSignalsPage from "./components/ManageIntentSignalsPage/ManageIntentSignalsPage";

const ManageIntentSignalsPageContainer = () => {
    const isIntroRoute = window.location.hash.includes(INTENT_SIGNALS_MANAGEMENT_INTRO_PAGE_URL);

    const dispatch = useDispatch();
    const isHighLevelAccount = salesSettingsHelper.isHighLevelAccount();
    const selectedTopics: readonly Topic[] = useSelector(selectIntentSelectedTopics);
    const isSelectedTopicLoading = useSelector(selectIsSelectedTopicFetching);
    const isUserApprovedTerms = useSelector(selectIsUserApprovedTerms);
    const intentApproval = localStorage.getItem("si:intent-approval");
    const isImpersonatingUser = salesSettingsHelper.isImpersonating();

    const isUserNotApproved = !intentApproval && !isUserApprovedTerms && !isImpersonatingUser;
    const showDataAgreements = isUserNotApproved && selectedTopics.length > 0;

    const {
        isOpenAdminApprovalModal,
        setIsOpenAdminApprovalModal,
        onCancelIntentManagementModal,
        onClickManagementApprovedTerms,
    } = useIntentManagementApprovedAction();

    useEffect(() => {
        void dispatch(fetchSelectedIntentTopicsThunk());
        if (!isHighLevelAccount) {
            void dispatch(fetchIntentUserAdminsThunk());
        }
    }, []);

    useEffect(() => {
        if (isSelectedTopicLoading) {
            return;
        }

        const shouldGoToIntro = isUserNotApproved && selectedTopics.length === 0 && !isIntroRoute;
        const shouldGoToMain = !isUserNotApproved && isIntroRoute;

        if (shouldGoToIntro) {
            void proNavigator.go(INTENT_SIGNALS_MANAGEMENT_INTRO_PAGE_ROUTE);
            return;
        }

        if (shouldGoToMain) {
            void proNavigator.go(INTENT_SIGNALS_MANAGEMENT_PAGE_ROUTE);
        }
    }, [isUserNotApproved, selectedTopics.length, isIntroRoute, isSelectedTopicLoading]);

    const onClickApproveUser = () => {
        onClickManagementApprovedTerms();
        void intentTrackingService.trackSelectedTopicsAction();
        void proNavigator.go(INTENT_SIGNALS_MANAGEMENT_PAGE_ROUTE);
    };

    const onClickVerification = () => {
        if (isUserNotApproved) {
            setIsOpenAdminApprovalModal(true);
        } else {
            void intentTrackingService.trackSelectedTopicsAction();
            void proNavigator.go(INTENT_SIGNALS_MANAGEMENT_PAGE_ROUTE);
        }
    };

    const handleBreadcrumbClick = ({ state }: { state: string }) => {
        void proNavigator.go(state);
    };

    const breadcrumbs = INTENT_SIGNALS_BREADCRUMBS.map((item) => ({ ...item, title: translate(item.title) }));

    return (
        <ErrorBoundary FallbackComponent={PageErrorState}>
            <SIBreadcrumbs
                list={breadcrumbs}
                onClick={handleBreadcrumbClick}
                dataAutomation="intent-management-breadcrumbs"
            />
            {isSelectedTopicLoading ? (
                <PageLoader isLoading={isSelectedTopicLoading} />
            ) : isIntroRoute ? (
                <IntroIntentSignalPage>
                    <IntroductionView onActionClick={onClickVerification} />
                </IntroIntentSignalPage>
            ) : (
                <ManageIntentSignalsPage>
                    <IntentTopicView />
                </ManageIntentSignalsPage>
            )}
            <ApplyTopicModal
                isOpen={isOpenAdminApprovalModal}
                customStyle={approvalModalConfig}
            >
                <TermsAndConditionsContent
                    title={translate("si.intent.management.modal.header.terms-and-conditions")}
                    description={translate("si.intent.management.modal.description.terms-and-conditions")}
                    cancelButtonText={translate("si.intent.management.modal.btn.cancel")}
                    applyButtonText={translate("si.intent.management.modal.btn.i-accepted")}
                    onClick={onClickApproveUser}
                    onCancelClick={onCancelIntentManagementModal}
                />
            </ApplyTopicModal>
            <ApplyTopicModal
                isOpen={showDataAgreements}
                customStyle={dataAgreementsModalConfig}
            >
                <ApplyDataAgreementsContent onApply={onClickApproveUser} />
            </ApplyTopicModal>
        </ErrorBoundary>
    );
};

export default withSecondaryBarSet("SalesIntelligenceIntent")(ManageIntentSignalsPageContainer);
